import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";

const COLLECTIONS = [
  {
    num: "01",
    title: "Community Collaborations",
    copy: "Partnerships with organizations built around shared story work. In collaboration with WREN (Women’s Rights & Empowerment Network), we provided $3,000 of testifier coaching to increase statehouse coverage on issues affecting South Carolinians.",
    testId: "stories-move-community",
  },
  {
    num: "02",
    title: "Storytelling in Action",
    copy: "Inspired Speaking — a public speaking course for professional women, led with Katie Zenger of Zenger Strategies at Femme X, Fall 2022. Strength-based, interactive sessions helping participants find their speaker style, own the stage, and master any speaking scenario.",
    testId: "stories-move-action",
    url: "https://creators.spotify.com/pod/profile/whatshesaidproject/episodes/Episode-24-Inspired-Speaking-with-Katie-Zenger-of-Zenger-Strategies-e1inas3",
    cta: "Hear About the Course →",
  },
];

const StoriesMovePage = () => {
  return (
    <>
      <Seo
        title="Stories Move US — #whatshesaidproject"
        description="Stories Move US is where storytelling meets intention, strategy, and impact — community collaborations and storytelling in action from #whatshesaidproject."
        path="/stories-move"
      />
      {/* HERO */}
      <section className="bg-white text-[#121212] pt-12 pb-24 md:py-28 border-b border-[#e5e5e5]">
        <div className="max-w-6xl mx-auto px-6">
          <span className="text-xs font-mono uppercase tracking-[.18em] text-[#e02020] block mb-3">
            Stories Move US
          </span>
          <h1
            data-testid="stories-move-headline"
            className="font-disp text-[clamp(36px,5vw,72px)] leading-[1.02] tracking-tight font-normal text-[#121212] text-balance max-w-4xl mb-6"
          >
            Every story has the potential to move something.
          </h1>
          <div className="max-w-2xl space-y-5">
            <p className="text-[17px] leading-[1.65] text-[#555555]">
              Stories Move US is where storytelling meets intention, strategy, and impact.
            </p>
            <p className="text-[17px] leading-[1.65] text-[#555555]">
              For more than a decade, #whatshesaidproject has helped people discover, develop, and
              share their stories in ways that honor their experiences and serve their goals.
            </p>
            <p className="text-[17px] leading-[1.65] text-[#555555]">
              We believe the power of a story lies not simply in telling it, but in{" "}
              <em className="text-[#121212]">choosing</em> when, how, why, and with whom to share
              it.
            </p>
          </div>
        </div>
      </section>

      {/* POSITIONING */}
      <section className="bg-[#fafafa] text-[#121212] py-24 md:py-32 border-b border-[#e5e5e5]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <span className="text-xs font-mono uppercase tracking-[.18em] text-[#e02020] block mb-3">
                The Practice
              </span>
              <h2 className="font-disp text-[clamp(28px,3.5vw,48px)] leading-[1.05] tracking-tight font-normal text-[#121212]">
                Intention, strategy, and impact.
              </h2>
            </div>
            <div className="lg:col-span-8 min-w-0 space-y-6 text-[17px] leading-[1.65] text-[#333333]">
              <p>
                Through coaching, creative collaborations, public storytelling, and community
                engagement, we explore how personal narratives can shape conversations, challenge
                assumptions, build connections, and inspire meaningful change.
              </p>
              <p className="text-[#555555]">
                Because stories do not just move us. They move the world around us.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* THE MIC METHOD */}
      <section className="bg-white text-[#121212] py-24 md:py-32 border-b border-[#e5e5e5]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <span className="text-xs font-mono uppercase tracking-[.18em] text-[#e02020] block mb-3">
                The Framework
              </span>
              <h2 className="font-disp text-[clamp(28px,3.5vw,48px)] leading-[1.05] tracking-tight font-normal text-[#121212]">
                The MIC Method™
              </h2>
            </div>
            <div className="lg:col-span-8 min-w-0">
              <p className="text-[17px] leading-[1.65] text-[#333333] mb-10">
                Message. Identity. Connection. A framework created by Shannon Ivey that turns lived
                experience into clear, compelling communication that builds trust and drives
                action.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {[
                  { title: "Message", copy: "Clarifying what your story is truly saying — and what it needs to do." },
                  { title: "Identity", copy: "Grounding the story in who you are, not who you think you should be." },
                  { title: "Connection", copy: "Shaping how the story meets its audience and builds trust." },
                ].map((pillar) => (
                  <div
                    key={pillar.title}
                    data-testid={`mic-pillar-${pillar.title.toLowerCase()}`}
                    className="border border-[#e5e5e5] bg-[#fafafa] p-8"
                  >
                    <span className="text-xs font-mono uppercase tracking-[.14em] text-[#e02020] block mb-3">
                      {pillar.title}
                    </span>
                    <p className="text-sm text-[#555555] leading-relaxed">{pillar.copy}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COLLECTIONS */}
      <section className="bg-white text-[#121212] py-24 md:py-32 border-b border-[#e5e5e5]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 pb-6 border-b border-[#121212]">
            <div>
              <span className="text-xs font-mono uppercase tracking-[.18em] text-[#e02020] block mb-2">
                The Work in Print &amp; Practice
              </span>
              <h2 className="font-disp text-[clamp(28px,3.5vw,48px)] leading-[1.05] tracking-tight font-normal text-[#121212]">
                Collections taking shape.
              </h2>
            </div>
            <p className="text-xs font-mono uppercase tracking-[.14em] text-[#6b6b6b] mt-4 md:mt-0">
              Real projects, growing collections
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {COLLECTIONS.map((c) => (
              <article
                key={c.num}
                data-testid={`stories-move-card-${c.testId}`}
                className="bg-white border border-[#e5e5e5] hover:border-[#121212] transition-colors p-8 md:p-10"
              >
                <span className="text-xs font-mono uppercase tracking-[.14em] text-[#e02020] block mb-4">
                  Collection {c.num}
                </span>
                <h3 className="font-disp text-2xl text-[#121212] mb-3">{c.title}</h3>
                <p className="text-sm text-[#555555] leading-relaxed">{c.copy}</p>
                {c.url && (
                  <a
                    href={c.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`${c.testId}-link`}
                    className="inline-flex items-center text-xs font-mono uppercase tracking-[.14em] text-[#121212] font-semibold underline underline-offset-4 hover:text-[#e02020] transition-colors mt-6"
                  >
                    {c.cta}
                  </a>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* COLLABORATORS */}
      <section className="bg-[#fafafa] text-[#121212] py-24 md:py-32 border-b border-[#e5e5e5]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-2xl mb-16">
            <span className="text-xs font-mono uppercase tracking-[.18em] text-[#e02020] block mb-2">
              Our Collaborators
            </span>
            <h2 className="font-disp text-[clamp(28px,3.5vw,48px)] leading-[1.05] tracking-tight font-normal text-[#121212]">
              Organizations we build with.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <a
              href="https://www.scwren.org/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="collaborator-wren"
              className="border border-[#e5e5e5] bg-white hover:border-[#121212] transition-colors p-10 flex flex-col items-center justify-center gap-3 min-h-[160px]"
            >
              <span className="font-disp text-2xl text-[#121212] tracking-tight">WREN</span>
              <span className="text-xs font-mono uppercase tracking-[.14em] text-[#6b6b6b] text-center">
                Women’s Rights &amp; Empowerment Network
              </span>
            </a>
            <a
              href="https://www.columbiamuseum.org/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="collaborator-cma"
              className="border border-[#e5e5e5] bg-white hover:border-[#121212] transition-colors p-10 flex flex-col items-center justify-center gap-4 min-h-[160px]"
            >
              <img
                src="/brand/columbia-museum.png"
                alt="Columbia Museum of Art"
                className="h-16 w-16 object-contain"
              />
              <span className="text-xs font-mono uppercase tracking-[.14em] text-[#6b6b6b] text-center">
                Columbia Museum of Art
              </span>
            </a>
            <a
              href="https://alternateroots.org/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="collaborator-alternate-roots"
              className="border border-[#e5e5e5] bg-white hover:border-[#121212] transition-colors p-10 flex flex-col items-center justify-center gap-4 min-h-[160px]"
            >
              <img
                src="/brand/alternate-roots.svg"
                alt="Alternate ROOTS"
                className="h-12 w-auto object-contain"
              />
            </a>
            <a
              href="https://www.southcarolinaarts.com/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="collaborator-scac"
              className="border border-[#e5e5e5] bg-white hover:border-[#121212] transition-colors p-10 flex flex-col items-center justify-center gap-4 min-h-[160px]"
            >
              <img
                src="/brand/sc-arts-commission.svg"
                alt="South Carolina Arts Commission"
                className="h-14 w-auto object-contain"
              />
              <span className="text-xs font-mono uppercase tracking-[.14em] text-[#6b6b6b] text-center">
                South Carolina Arts Commission
              </span>
            </a>
          </div>
        </div>
      </section>
      <section className="bg-[#121212] text-white py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-8">
              <span className="text-xs font-mono uppercase tracking-[.18em] text-[#e02020] block mb-3">
                Collaborate on Stories Move
              </span>
              <h2 className="font-disp text-[clamp(28px,3.5vw,48px)] leading-[1.05] tracking-tight font-normal text-white mb-6">
                Bring story work to your community or institution.
              </h2>
              <p className="text-[17px] text-[#aaaaaa] leading-relaxed max-w-2xl">
                Story development, intentional sharing, and public engagement — tell us what you
                are building.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link
                to="/connect"
                data-testid="stories-move-cta-connect"
                className="inline-flex items-center justify-center px-6 py-3.5 bg-[#e02020] text-white text-xs font-mono uppercase tracking-[.14em] hover:bg-white hover:text-[#121212] transition-colors rounded-none"
              >
                Start an Inquiry →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default StoriesMovePage;
